import { useState, useEffect } from 'react';
import { ArrowLeft, Play, Pause, X, VolumeX, PlayCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface MeditationProps {
  onBack: () => void;
}

type MeditationType = 'mindfulness' | 'breathing' | 'body_scan' | 'visualization';
type SoundType = 'vedic' | 'silent';
type PracticeMode = 'learn' | 'pro';

const meditationTypes = [
  { type: 'mindfulness' as MeditationType, emoji: '🧘', label: 'Mindfulness', desc: 'Present moment awareness' },
  { type: 'breathing' as MeditationType, emoji: '🌬️', label: 'Breathing', desc: 'Focused breath work' },
  { type: 'body_scan' as MeditationType, emoji: '✨', label: 'Body Scan', desc: 'Progressive relaxation' },
  { type: 'visualization' as MeditationType, emoji: '🌅', label: 'Visualization', desc: 'Guided imagery' }
];

export default function Meditation({ onBack }: MeditationProps) {
  const [meditationType, setMeditationType] = useState<MeditationType>('mindfulness');
  const [duration, setDuration] = useState(10);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [streak, setStreak] = useState(0);
  const [weeklyStats, setWeeklyStats] = useState({ total_sessions: 0, total_minutes: 0 });
  const [practiceMode, setPracticeMode] = useState<PracticeMode>('pro');
  const [showSoundModal, setShowSoundModal] = useState(false);
  const [showLearnModal, setShowLearnModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedSound, setSelectedSound] = useState<SoundType | null>(null);
  const [isClosingModal, setIsClosingModal] = useState(false);

  useEffect(() => {
    loadStreak();
    loadWeeklyStats();
  }, []);

  useEffect(() => {
    let interval: number | undefined;
    if (isActive && !isPaused && timeElapsed < duration * 60) {
      interval = setInterval(() => {
        setTimeElapsed(prev => {
          if (prev + 1 >= duration * 60) {
            handleComplete();
            return duration * 60;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, isPaused, timeElapsed, duration]);

  const loadStreak = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('get_activity_streak', {
        p_user_id: user.id,
        p_activity_type: 'meditation'
      });

      if (!error && data !== null) {
        setStreak(data);
      }
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadWeeklyStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('get_activity_weekly_stats', {
        p_user_id: user.id,
        p_activity_type: 'meditation'
      });

      if (!error && data && data.length > 0) {
        setWeeklyStats(data[0]);
      }
    } catch (error) {
      console.error('Error loading weekly stats:', error);
    }
  };

  const handleStartClick = () => {
    if (practiceMode === 'learn') {
      setShowLearnModal(true);
    } else {
      setShowSoundModal(true);
    }
  };

  const handleSoundSelect = (type: SoundType) => {
    setSelectedSound(type);
    setIsClosingModal(true);
    setTimeout(() => {
      setShowSoundModal(false);
      setIsClosingModal(false);
      setIsActive(true);
      setIsPaused(false);
      setTimeElapsed(0);
    }, 300);
  };

  const closeSoundModal = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowSoundModal(false);
      setIsClosingModal(false);
    }, 300);
  };

  const closeLearnModal = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowLearnModal(false);
      setIsClosingModal(false);
    }, 300);
  };

  const handleComplete = async () => {
    setIsActive(false);
    setShowResultsModal(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('mind_body_activities').insert({
        user_id: user.id,
        activity_type: 'meditation',
        duration_minutes: Math.ceil(timeElapsed / 60),
        difficulty_level: meditationType,
        completed_at: new Date().toISOString()
      });

      await loadStreak();
      await loadWeeklyStats();
    } catch (error) {
      console.error('Error saving meditation session:', error);
    }
  };

  const handleEndSession = () => {
    setIsActive(false);
    setIsPaused(false);
    setTimeElapsed(0);
    setSelectedSound(null);
  };

  const closeResultsModal = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      setTimeElapsed(0);
      setSelectedSound(null);
    }, 300);
  };

  const handleMeditateAgain = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      setTimeElapsed(0);
      setShowSoundModal(true);
    }, 300);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = (timeElapsed / (duration * 60)) * 100;

  const getBreathingPhase = () => {
    const cycle = timeElapsed % 16;
    if (cycle < 4) return { phase: 'Breathe In', emoji: '🌬️' };
    if (cycle < 8) return { phase: 'Hold', emoji: '⏸️' };
    if (cycle < 12) return { phase: 'Breathe Out', emoji: '💨' };
    return { phase: 'Hold', emoji: '⏸️' };
  };

  // Active Session View
  if (isActive) {
    const breathPhase = getBreathingPhase();
    
    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <header className="bg-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
            <button
              onClick={handleEndSession}
              className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xl">🧘</span>
              <h1 className="text-base font-bold text-gray-900">Meditation</h1>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-cyan-50 rounded-lg">
              {selectedSound === 'vedic' ? (
                <span className="text-xs">🕉️</span>
              ) : (
                <VolumeX size={12} className="text-cyan-600" />
              )}
              <span className="text-[10px] font-bold text-cyan-600 uppercase">
                {selectedSound === 'vedic' ? 'Vedic' : 'Silent'}
              </span>
            </div>
          </header>

          <div className="p-4 space-y-4">
            {/* Timer Circle */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <svg className="w-48 h-48 transform -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="#e5e7eb"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="url(#meditationGradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 88}`}
                      strokeDashoffset={`${2 * Math.PI * 88 * (1 - progress / 100)}`}
                      strokeLinecap="round"
                      className="transition-all duration-300"
                    />
                    <defs>
                      <linearGradient id="meditationGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#06b6d4" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-4xl font-black text-gray-900">{formatTime(timeElapsed)}</p>
                      <p className="text-sm text-gray-500">of {duration} min</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Type & Breathing Guide */}
              <div className="text-center mb-6">
                <span className="text-3xl mb-2 block">
                  {meditationTypes.find(m => m.type === meditationType)?.emoji}
                </span>
                <p className="font-bold text-gray-900">
                  {meditationTypes.find(m => m.type === meditationType)?.label}
                </p>
                
                {meditationType === 'breathing' && (
                  <div className="mt-4 bg-cyan-50 rounded-xl p-3 border border-cyan-200">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-xl animate-pulse">{breathPhase.emoji}</span>
                      <span className="font-bold text-cyan-700 animate-pulse">{breathPhase.phase}</span>
                    </div>
                    <div className="h-1.5 bg-cyan-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-1000"
                        style={{ width: `${((timeElapsed % 16) / 16) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Control Buttons */}
              <div className="flex gap-3">
                {isPaused ? (
                  <button
                    onClick={() => setIsPaused(false)}
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-xl font-bold text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <Play size={18} fill="currentColor" />
                    Resume
                  </button>
                ) : (
                  <button
                    onClick={() => setIsPaused(true)}
                    className="flex-1 bg-gray-900 text-white py-4 rounded-xl font-bold text-sm transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <Pause size={18} fill="currentColor" />
                    Pause
                  </button>
                )}
                <button
                  onClick={handleEndSession}
                  className="px-6 py-4 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98]"
                >
                  End
                </button>
              </div>
            </div>

            {/* Tips Card */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-2xl p-4">
              <p className="text-xs font-bold text-cyan-700 mb-2">💡 TIP</p>
              <p className="text-sm text-cyan-800">
                {meditationType === 'mindfulness' && "Focus on your breath. When your mind wanders, gently bring it back."}
                {meditationType === 'breathing' && "Breathe in for 4 counts, hold for 4, exhale for 4, hold for 4. Repeat."}
                {meditationType === 'body_scan' && "Start from your toes and slowly move attention up through your body."}
                {meditationType === 'visualization' && "Imagine a peaceful place. Notice the colors, sounds, and sensations."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main View
  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <header className="bg-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xl">🧘</span>
            <h1 className="text-base font-bold text-gray-900">Meditation</h1>
          </div>
          <div className="w-9"></div>
        </header>

        <div className="p-4 space-y-4">
          {/* Stats Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base">🔥</span>
                <p className="text-xs font-medium text-gray-500">Streak</p>
              </div>
              <p className="text-2xl font-black text-gray-900">{streak} <span className="text-sm font-medium text-gray-400">days</span></p>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base">⏱️</span>
                <p className="text-xs font-medium text-gray-500">This Week</p>
              </div>
              <p className="text-2xl font-black text-gray-900">{weeklyStats.total_minutes || 0} <span className="text-sm font-medium text-gray-400">mins</span></p>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="bg-white rounded-2xl p-4 border border-gray-200">
            <p className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Select Mode</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setPracticeMode('learn')}
                className={`p-4 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  practiceMode === 'learn'
                    ? 'border-cyan-500 bg-cyan-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${
                  practiceMode === 'learn' ? 'bg-cyan-500' : 'bg-gray-100'
                }`}>
                  <span className="text-xl">{practiceMode === 'learn' ? '📖' : '📚'}</span>
                </div>
                <p className={`font-bold text-sm ${practiceMode === 'learn' ? 'text-cyan-700' : 'text-gray-700'}`}>Learn Mode</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Guided tutorial</p>
              </button>

              <button
                onClick={() => setPracticeMode('pro')}
                className={`p-4 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  practiceMode === 'pro'
                    ? 'border-cyan-500 bg-cyan-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${
                  practiceMode === 'pro' ? 'bg-cyan-500' : 'bg-gray-100'
                }`}>
                  <span className="text-xl">{practiceMode === 'pro' ? '⚡' : '🎯'}</span>
                </div>
                <p className={`font-bold text-sm ${practiceMode === 'pro' ? 'text-cyan-700' : 'text-gray-700'}`}>Pro Mode</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Timed practice</p>
              </button>
            </div>
          </div>

          {/* Duration Selection */}
          <div className="bg-white rounded-2xl p-4 border border-gray-200">
            <p className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Duration</p>
            <div className="grid grid-cols-4 gap-2">
              {[5, 10, 15, 20].map((min) => (
                <button
                  key={min}
                  onClick={() => setDuration(min)}
                  className={`py-2.5 rounded-xl font-bold text-sm transition-all active:scale-[0.95] ${
                    duration === min
                      ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/20'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {min}m
                </button>
              ))}
            </div>
          </div>

          {/* Meditation Type Selection */}
          <div className="bg-white rounded-2xl p-4 border border-gray-200">
            <p className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">Meditation Type</p>
            <div className="grid grid-cols-2 gap-2">
              {meditationTypes.map((item) => (
                <button
                  key={item.type}
                  onClick={() => setMeditationType(item.type)}
                  className={`p-3 rounded-xl flex items-center gap-3 transition-all active:scale-[0.98] border-2 ${
                    meditationType === item.type
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">{item.emoji}</span>
                  <div className="text-left">
                    <p className={`font-semibold text-sm ${meditationType === item.type ? 'text-cyan-700' : 'text-gray-900'}`}>
                      {item.label}
                    </p>
                    <p className="text-[10px] text-gray-500">{item.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Start Button */}
          <button
            onClick={handleStartClick}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-xl font-bold text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <span className="text-lg">▶️</span>
            Start {practiceMode === 'learn' ? 'Learning' : 'Meditation'}
          </button>
        </div>
      </div>

      {/* Sound Selection Modal */}
      {showSoundModal && (
        <div 
          className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
            isClosingModal ? 'bg-black/0' : 'bg-black/50'
          }`}
          onClick={closeSoundModal}
        >
          <div 
            className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
              isClosingModal ? 'translate-y-full' : 'translate-y-0'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-5 pb-6">
              <div className="text-center mb-5">
                <div className="w-14 h-14 bg-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">🎵</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Choose Your Sound</h2>
                <p className="text-sm text-gray-500 mt-1">Select background for your meditation</p>
              </div>

              <div className="space-y-3 mb-5">
                <button
                  onClick={() => handleSoundSelect('vedic')}
                  className="w-full p-4 rounded-2xl border-2 border-gray-200 hover:border-cyan-300 hover:bg-cyan-50 transition-all flex items-center gap-4 active:scale-[0.98]"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🕉️</span>
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-bold text-gray-900">Vedic Ancient Chanting</p>
                    <p className="text-xs text-gray-500">Traditional Sanskrit mantras</p>
                  </div>
                </button>

                <button
                  onClick={() => handleSoundSelect('silent')}
                  className="w-full p-4 rounded-2xl border-2 border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-all flex items-center gap-4 active:scale-[0.98]"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-600 rounded-xl flex items-center justify-center">
                    <VolumeX size={24} className="text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-bold text-gray-900">Silent Meditation</p>
                    <p className="text-xs text-gray-500">No background sound</p>
                  </div>
                </button>
              </div>

              <button
                onClick={closeSoundModal}
                className="w-full py-3 text-gray-500 font-semibold text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learn Mode Modal */}
      {showLearnModal && (
        <div 
          className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
            isClosingModal ? 'bg-black/0' : 'bg-black/50'
          }`}
          onClick={closeLearnModal}
        >
          <div 
            className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
              isClosingModal ? 'translate-y-full' : 'translate-y-0'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-5 pb-6">
              <div className="text-center mb-5">
                <div className="w-14 h-14 bg-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">📖</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Learn Meditation</h2>
                <p className="text-sm text-gray-500 mt-1">Listen to guided voice tutorials</p>
              </div>

              {/* Audio Placeholder */}
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl aspect-video flex flex-col items-center justify-center mb-5 border-2 border-dashed border-gray-300">
                <div className="w-16 h-16 bg-white/80 rounded-full flex items-center justify-center mb-3 shadow-lg">
                  <PlayCircle size={32} className="text-gray-400" />
                </div>
                <p className="text-gray-500 font-semibold">Audio Guide Coming Soon</p>
                <p className="text-xs text-gray-400 mt-1">Learn proper meditation techniques</p>
              </div>

              {/* Meditation Types Preview */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <p className="text-xs font-bold text-gray-500 mb-3">TECHNIQUES YOU'LL LEARN</p>
                <div className="grid grid-cols-2 gap-2">
                  {meditationTypes.map((item) => (
                    <div
                      key={item.type}
                      className="bg-white rounded-lg p-2 flex items-center gap-2 border border-gray-200"
                    >
                      <span className="text-lg">{item.emoji}</span>
                      <span className="text-xs font-medium text-gray-700">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={closeLearnModal}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-xl font-bold text-sm active:scale-[0.98] transition-all shadow-lg shadow-cyan-500/20"
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResultsModal && (
        <div 
          className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
            isClosingModal ? 'bg-black/0' : 'bg-black/50'
          }`}
          onClick={closeResultsModal}
        >
          <div 
            className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
              isClosingModal ? 'translate-y-full' : 'translate-y-0'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-5 pb-6">
              {/* Celebration */}
              <div className="text-center mb-5">
                <div className="text-6xl mb-2">🎉</div>
                <h2 className="text-xl font-bold text-gray-900">Session Complete!</h2>
                <p className="text-sm text-gray-500">Great job on your meditation</p>
              </div>

              {/* Stats */}
              <div className="bg-cyan-50 border border-cyan-200 rounded-2xl p-4 mb-5">
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <p className="text-3xl font-black text-cyan-600">{Math.ceil(timeElapsed / 60)}</p>
                    <p className="text-xs text-cyan-600 font-medium">Minutes</p>
                  </div>
                  <div className="w-px h-12 bg-cyan-200"></div>
                  <div className="text-center flex-1">
                    <p className="text-3xl">
                      {meditationTypes.find(m => m.type === meditationType)?.emoji}
                    </p>
                    <p className="text-xs text-cyan-600 font-medium">
                      {meditationTypes.find(m => m.type === meditationType)?.label}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={closeResultsModal}
                  className="py-3.5 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98]"
                >
                  Done
                </button>
                <button
                  onClick={handleMeditateAgain}
                  className="py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20 transition-all active:scale-[0.98]"
                >
                  Meditate Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}